'use client';

import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useContext } from 'react';
import { QueryApi } from '@/features/query/api';
import { PAGINATION_LIMIT } from '@/shared/config/constants/api';
import { EventService } from '@/shared/lib/event-service';
import { Button } from '@/shared/ui/button';
import { Icon } from '@/shared/ui/icon';
import './product-detail.scss';
import { ProductDetailContext } from './provider';

type Props = {
    variant?: 'overlay' | 'inline';
};

export const ProductDetailGalleryActions = ({ variant = 'overlay' }: Props) => {
    const { currentItem } = useContext(ProductDetailContext);
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const favoriteItems = useQuery(
        QueryApi.Customer.queries.FavoriteItems({ offset: 0, limit: PAGINATION_LIMIT }),
    );
    const isInFavorites = favoriteItems.data?.items?.some((item) => item.itemId === currentItem?.id);

    const handleBack = () => {
        EventService.trigger('router:back');
    };

    const handleToggleFavorite = () => {
        if (!customerQuery.data) {
            EventService.trigger('account:auth:view-detail');
        } else if (isInFavorites) {
            EventService.trigger('account:product:remove-from-favorite', { id: currentItem!.id });
        } else {
            EventService.trigger('account:product:add-to-favorite', { id: currentItem!.id });
        }
    };

    return (
        <div
            className={clsx('product-detail__gallery-actions', `product-detail__gallery-actions--${variant}`)}
        >
            <Button
                variant="ghost"
                className="product-detail__gallery-actions-back-button"
                onClick={handleBack}
            >
                <Icon
                    name="arrow-down"
                    width={24}
                    height={24}
                    className="product-detail__gallery-actions-back-icon"
                />
            </Button>
            <Button
                variant="ghost"
                className={clsx(
                    'product-detail__gallery-actions-favorite-button',
                    isInFavorites && 'product-detail__gallery-actions-favorite-button--favorite',
                )}
                onClick={handleToggleFavorite}
            >
                {isInFavorites ? (
                    <Icon name="heart-fill" width={24} height={24} />
                ) : (
                    <Icon name="heart" width={24} height={24} />
                )}
            </Button>
        </div>
    );
};

export const ProductDetailGalleryActionsInfoFallback = () => {
    const { currentItem } = useContext(ProductDetailContext);
    if (currentItem?.assets?.length) {
        return null;
    }
    return <ProductDetailGalleryActions variant="inline" />;
};
